/** @jsxImportSource @emotion/react */
import { css, jsx, CSSObject } from '@emotion/react';
import { FormatOptionLabelMeta, SelectProps } from 'components/select';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'components/i18n';
import { useQuery } from 'react-query';
import Select from '../index';
import styles from '../styles';
import Option from '../Option';
import api from '../../../libs/api';
import VatCodeName from '../../../modules/sale/components/VatCodeName';
import { Customer, VatCode } from '../../../modules/sale/types';
import getVatCodeName from '../../../modules/sale/getVatCodeName';
import { isEU, isNL } from '../../../libs/countries';
import { useCurrentCompany } from '../../../modules/auth/CurrentCompany';
import _ from 'lodash';

export const filterVatCodes = (vatCodes: VatCode[], customer?: Customer | null, companyVatPeriod?: string | null) => {

  const countryCode = customer?.address?.country || '';
  let filteredVatCodes = vatCodes;
  filteredVatCodes =  _.orderBy(filteredVatCodes,
    [(vatCode) => vatCode.id.match(/-NVT/i), 'category', 'contraAccount'],
    ['desc', 'asc', 'asc']
  );
  const filter = (pattern: RegExp) => filteredVatCodes.filter(vatCode => !!vatCode.id.match(pattern));

  if(companyVatPeriod === 'EXEMPTED') {
    return filter(/-NVT$/i);
  }
  if(companyVatPeriod === 'EXEMPTED_KOR') {
    return filter(/-KOR$/i);
  }
  filteredVatCodes = filteredVatCodes.filter(vatCode => !vatCode.id.match(/-(KOR)$/i));

  if(!customer || !countryCode) {
    return filteredVatCodes;
  }

  if(customer?.type==='private') {
    if(isNL(countryCode)) {
      return filter(/-NL-/i);
    }
    const countryVatCodes = filter(RegExp(`-${countryCode}-`,'i'));
    if(countryVatCodes.length > 0) {
      return filter(/-NL-/i).concat(countryVatCodes); // for example BE (Belgium) and all NL vat codes
    }
    return filter(/-EX-/i);
  }

  if(customer?.type==='business') {
    if(isNL(countryCode)) {
      return filter(/-NL-/i);
    }
    if(isEU(countryCode)) {
      return filter(/-EU-/i);
    }
    if(!isEU(countryCode)) {
      return filter(/-EX-/i);
    }
  }

  return filteredVatCodes;

};

// the text node of the control
const controlLabel = (vatCode: VatCode, date: Date) => (
  <Option><VatCodeName vatCode={vatCode} date={date}/></Option>
);

// the text node for an option
const optionLabel = (vatCode: VatCode, date: Date) => {
  return (
    <Option></Option>
  );
};

// switch formatters based on render context (menu | value)
const formatOptionLabel = (date: Date) => (
  option: VatCode,
  { context }: FormatOptionLabelMeta<VatCode>
) => {
  return controlLabel(option,date);
  // return context === 'value' ? controlLabel(option, date) : optionLabel(option, date);
};

const vatSelectStyles = {
  ...styles,
  menu: (style: any) => ({ ...style, width: 200 /* right:0 */ }),
};

// interface Props<OptionType> extends SelectProps<OptionType> {
// }

export const fetchVatCodes = async (key: string, sale: boolean, purchase: boolean) => {
  const whereFilter: {sale?: boolean, purchase?: boolean} = {};
  if(sale) whereFilter.sale = true;
  if(purchase) whereFilter.purchase = true;
  const vatCodes = await api.get('/vat-codes', {params: { filter: {where: whereFilter, order: 'category ASC'} }});
  // @ts-ignore
  return vatCodes as VatCode[];
};

export default function VatCodeSelect(props: any) {
  const {sale, purchase, date, value, onChange, customer, ...rest } = props;
  const { data: vatCodes } = useQuery(['vatCodes', sale, purchase], fetchVatCodes, {staleTime: 1000 * 60 * 30 });
  const intl = useIntl();
  const currentCompany = useCurrentCompany();

  const valueObject = useMemo(()=> {
    console.log('value',value);
    if(!vatCodes) return null;
    return (vatCodes as VatCode[]).find(vatCode => vatCode.id === value) || null;
  },[vatCodes, value]);

  const handleChange = useCallback((vatCode)=> {
    if(onChange) onChange(vatCode.id)
  },[]);

  if(!vatCodes) return null;

  const filteredVatCodes = filterVatCodes(vatCodes, customer, currentCompany?.vatPeriod);

  return (
    <Select<VatCode>
      value={valueObject}
      options={filteredVatCodes}
      onChange={handleChange}
      getOptionLabel={(option) => getVatCodeName({intl, vatCode: option}) || ''}
      getOptionValue={(option) => option.id}
      formatOptionLabel={formatOptionLabel(date)}
      styles={vatSelectStyles}
      appearance="subtle"
      {...rest}
    />
  );
}
